import { container, common, createCookie, createDeepLink } from '@mtfe/sweet-utils';
import { NewLoginModal, Toast } from '@mtfe/sweet-components';
import { loadLogin, oadLX, oadWX, getOadParams, loadKNB } from '@mtfe/pr-oad';
import { addLoginError, addLoginSuccess, getErrorMessage, LoginSource } from '@mtfe/sweet-report';

const { isMiniProgram, isInTuanApp, isMiniProgramTuan, isMiniProgramWaimai, isInWaiMaiApp } = container;
const { isFunction, qsParse } = common;
const autoLoginTryJump = 'b_waimai_s7vaurel_mc';

const TOKEN_KEY = 'mt_c_token';
const Cookies = createCookie();

const deeplink = createDeepLink();

const generateQS = (params = {}) => {
    const qs = Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join('&');
    return qs || '';
};

const buildScheme = async (url, urlParams) => {
    if (!url) {
        throw new Error('url cannot be empty');
    }

    const [urlWithOutHashInit, urlHash] = url.split('#');
    let urlWithOutHash = urlWithOutHashInit;

    const oadParams = await getOadParams();
    if (oadParams.redirectCount) {
        urlParams.redirectCount = oadParams.redirectCount + 1;
    } else {
        urlParams.redirectCount = 2; // 二跳参数
    }

    const iMeiTuanUrlParamsString = generateQS(urlParams);

    if (iMeiTuanUrlParamsString) {
        const markIndex = urlWithOutHash.indexOf('?');
        if (markIndex === -1) {
            urlWithOutHash = `${urlWithOutHash}?${iMeiTuanUrlParamsString}`;
        } else {
            urlWithOutHash = `${urlWithOutHash}&${iMeiTuanUrlParamsString}`;
        }
    }

    const webUrl = encodeURIComponent(urlHash ? `${urlWithOutHash}#${urlHash}` : urlWithOutHash);
    const iMeiTuanUrl = `imeituan://www.meituan.com/web?url=${webUrl}&mt_ret_to=waimai_home`;

    return iMeiTuanUrl;
};

/**
 * 自动跳转到团app，此版本未对小程序环境做单独处理
 * @param {object} option 跳转的选项
 * @param {object} urlParams 跳转参数，跳转的时候会添加在URL里面
 * @param {Array} args 执行函数的参数
 */
const autoLogin = async (option = {}, urlParams = {}, args = []) => {
    let { url } = option;
    const { loginSuccessCb, phone, cb, modalTitle = '领红包需要验证您的身份' } = option;
    // 无论在任何环境，如果登录过了，不执行后续逻辑
    if (phone) {
        if (isFunction(cb)) {
            // eslint-disable-next-line
            return cb.apply(null, args);
        }
        return;
    }
    if (!url) {
        url = window.location.href;
    }

    // 没有登录过的情况
    // 在团APP里面
    if (isInTuanApp || isInWaiMaiApp) {
        try {
            const KNB = await loadKNB();
            KNB.login({
                success() {
                    addLoginSuccess(LoginSource.knb);
                    // 是否需要重新加载页面
                    if (isFunction(loginSuccessCb)) {
                        loginSuccessCb();
                    }
                },
                fail() {
                    addLoginError(LoginSource.knb, 'knb登录失败');
                    Toast({ message: '登录失败，请重新登录', position: 'center' });
                },
            });
        } catch (error) {
            addLoginError(LoginSource.knb, 'knb登录失败');
            Toast({ message: '登录失败，请重新登录', position: 'center' });
        }
    } else if (isMiniProgram) {
        // (团小程序 || 外卖小程序)并且没拒绝过
        if (isMiniProgramTuan || isMiniProgramWaimai) {
            try {
                if (oadWX.isMiniProgramLoginReject) {
                    throw Error('已存在小程序登录跳转标识');
                } else {
                    if (!oadWX.isMiniProgramLoginForce) {
                        // 如果没有客户端强制登录标识或者没有登录状态，强制登录
                        oadWX.miniProgramLogin(window.location.href);
                    } else {
                        if (!oadWX.hasWxappLoginStatus()) {
                            // 客户端登陆异常兜底
                            addLoginError(LoginSource.miniProgram, 'wxapp_login_status缺失，没有登录状态');
                            NewLoginModal({ loadLogin, loginSuccessCb, modalTitle });
                        }
                    }
                }
            } catch (error) {
                // 上报小程序登录异常
                addLoginError(LoginSource.miniProgram, getErrorMessage(error, '小程序登录，未知错误!'));
                // 此处兜底处理，显示页面内登录框
                NewLoginModal({ loadLogin, loginSuccessCb, modalTitle });
            }
            return;
        }

        // 延迟出现登录弹窗
        setTimeout(() => {
            addLoginError(LoginSource.miniProgram, '出现兜底弹窗，小程序跳转登录失败');
            NewLoginModal({ loadLogin, loginSuccessCb, modalTitle });
        }, 1200);
    } else {
        try {
            const iMeiTuanUrl = await buildScheme(url, urlParams);
            deeplink.gotoDp(iMeiTuanUrl, '', null, {
                deepLinkSuccess: () => {
                    addLoginSuccess(LoginSource.wxCallApp);
                    setTimeout(() => {
                        NewLoginModal({ loadLogin, loginSuccessCb, modalTitle });
                    }, 500);
                },
                deepLinkFail: () => {
                    addLoginError(LoginSource.wxCallApp, 'wx deeplink 跳转登录失败');
                    NewLoginModal({ loadLogin, loginSuccessCb, modalTitle });
                },
            });
        } catch (error) {
            addLoginError(LoginSource.wxCallApp, getErrorMessage(error, '唤起 APP 登录，未知错误'));
            NewLoginModal({ loadLogin, loginSuccessCb, modalTitle });
        } finally {
            oadLX.mc(autoLoginTryJump);
        }
    }
};

// 2.1【页面加载完自动触发】团系容器(团App、外卖App)，自动将url中的token写入cookie
if (isInTuanApp || isInWaiMaiApp) {
    try {
        const qsObj = qsParse(window.location.search.substring(1));
        if (qsObj) {
            const token = qsObj.wm_logintoken || qsObj.token || qsObj.mt_c_token || '';
            if (token) {
                addLoginSuccess(LoginSource.app);
                Cookies.set(TOKEN_KEY, token);
            }
        }
    } catch (error) {
        addLoginError(LoginSource.app, error.message || 'App容器初始化token error!');
        console.error('App容器初始化token error!');
    }
}

// 2.1【页面加载完自动触发】团系小程序（团、外卖），自动将url中的token写入cookie
if (isMiniProgramTuan || isMiniProgramWaimai) {
    oadWX.miniProgramLoginReady();
}

// 小程序环境强制登录（为了与小程序登录态保持一致）
const oadForceMiniProgramLogin = () => {
    if (isMiniProgramTuan || isMiniProgramWaimai) {
        if (!oadWX.isMiniProgramLoginForce) {
            // 如果没有客户端强制登录标识或者没有登录状态，强制登录
            oadWX.miniProgramLogin(window.location.href);
        } else {
            if (!oadWX.hasWxappLoginStatus()) {
                addLoginError(LoginSource.miniProgram, 'wxapp_force_login参数已存在，但是没有登录状态');
            }
        }
    }
};

export { oadForceMiniProgramLogin };

export default autoLogin;
